import React, { useMemo, useEffect, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Avatar,
  Icon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { MdSettings, MdLogout, MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useNavbarAction } from "contexts/NavbarActionContext";
import { getUser, logout } from "api/api";

function UserProfileCard() {
  const navigate = useNavigate();
  const { userData } = useNavbarAction();
  const [fetchedUserData, setFetchedUserData] = useState(null);

  // Fetch user data from API on mount
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const data = await getUser();
        setFetchedUserData(data);
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    fetchUserData();
  }, []);

  // Déterminer les données à afficher avec fallback
  const displayUser = useMemo(() => {
    // 1. Utiliser les données du contexte si disponibles
    if (userData && (userData.firstname || userData.nom)) {
      return {
        firstname: userData.firstname || userData.nom || "User",
        lastname: userData.lastname || "",
        role: userData.role || userData.rôle || "Staff",
        photo: userData.photo || null,
      };
    }

    // 2. Utiliser les données fetch\u00e9es de l'API
    if (fetchedUserData) {
      const firstname = fetchedUserData.firstname || fetchedUserData.first_name || fetchedUserData.nom || "User";
      const lastname = fetchedUserData.lastname || fetchedUserData.last_name || "";
      const role = fetchedUserData.role || fetchedUserData.user_type || fetchedUserData.rôle || "Staff";
      const photo = fetchedUserData.photo || fetchedUserData.avatar || null;

      return {
        firstname,
        lastname,
        role,
        photo,
      };
    }

    // 3. Fallback au localStorage si contexte et API sont vides
    try {
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        const firstname = parsedUser.firstname || parsedUser.first_name || parsedUser.nom || "User";
        const lastname = parsedUser.lastname || parsedUser.last_name || "";
        const role = parsedUser.role || parsedUser.user_type || parsedUser.rôle || "Staff";
        const photo = parsedUser.photo || parsedUser.avatar || null;

        return {
          firstname,
          lastname,
          role,
          photo,
        };
      }
    } catch (error) {
      console.error("Error loading user data from localStorage:", error);
    }

    // 4. Fallback final
    return {
      firstname: "User",
      lastname: "Profile",
      role: "Staff",
      photo: null,
    };
  }, [userData, fetchedUserData]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Logout error:", error);
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/auth/sign-in");
  };

  const userName = `${displayUser.firstname} ${displayUser.lastname}`;
  const userRole = displayUser.role;
  const userAvatar = displayUser.photo;

  return (
    <Box
      w="100%"
      px="16px"
      py="16px"
      flexShrink={0}
      borderTop="1px solid rgba(255, 255, 255, 0.10)">
      <Flex
        align="center"
        justify="space-between"
        bg="rgba(255, 255, 255, 0.08)"
        p="12px"
        borderRadius="12px"
        gap="12px">

        {/* User Info */}
        <Flex
          direction="column"
          align="flex-start"
          justify="center"
          flex="1"
          minW="0">
          <Text
            fontSize="12px"
            fontWeight="600"
            color="white"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            fontFamily="Poppins, sans-serif">
            {userName}
          </Text>
          <Text
            fontSize="11px"
            fontWeight="400"
            color="rgba(255, 255, 255, 0.70)"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            fontFamily="Poppins, sans-serif">
            {userRole}
          </Text>
        </Flex>

        {/* Settings Menu */}
        <Menu>
          <MenuButton
            as={Box}
            cursor="pointer"
            p="4px"
            borderRadius="6px"
            _hover={{ bg: "rgba(255, 255, 255, 0.15)" }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            transition="0.2s">
            <Icon
              as={MdSettings}
              w="18px"
              h="18px"
              color="white"
              _hover={{ color: "#B62B30" }}
            />
          </MenuButton>
          <MenuList
            bg="#1a1a2e"
            borderColor="rgba(255, 255, 255, 0.15)"
            boxShadow="0 8px 16px rgba(0, 0, 0, 0.2)">
            <MenuItem
              bg="transparent"
              _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
              color="white"
              fontSize="13px"
              icon={<MdSettings w="16px" h="16px" />}
              onClick={() => navigate("/admin/profile")}>
              Paramètres
            </MenuItem>
            <MenuItem
              bg="transparent"
              _hover={{ bg: "rgba(255, 255, 255, 0.1)" }}
              color="#B62B30"
              fontSize="13px"
              icon={<MdLogout w="16px" h="16px" />}
              onClick={handleLogout}>
              Déconnexion
            </MenuItem>
          </MenuList>
        </Menu>
      </Flex>
    </Box>
  );
}

export default UserProfileCard;
